import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from 'react-router-dom';
import { PRODUCTION } from '@shared/environments';
import { sentryCreateBrowserRouter } from '@utils/sentry';
import { featureFlagConfig } from './featureFlags';
import { AvailabilityContextProvider } from './context/AvailabilityContext';
import { GuestPriceUpdates } from './context/GuestPriceUpdates';
import { GuestPricingBrowseContextProvider } from './pricingAnalytics/GuestPricingBrowseContext';
import { RestaurantContextProvider } from './context/RestaurantContext';
import { AccountsLayout } from './accounts/AccountsLayout';
import { BalancePage } from './accounts/BalancePage';
import { EmailPage } from './accounts/EmailPage';
import { NotificationsPage } from './accounts/NotificationsPage';
import { PaymentMethodsPage } from './accounts/PaymentMethodsPage';
import { IsAuthenticated } from './auth/IsAuthenticated';
import { LoginPage } from './auth/LoginPage';
import { FetchLinkToken, PlaidLink } from './bankAccounts';
import { OfferCheckoutConfirmationPage } from './checkout/OfferCheckoutConfirmationPage';
import { OfferCheckoutLayout } from './checkout/OfferCheckoutLayout';
import { OfferCheckoutPage } from './checkout/OfferCheckoutPage';
import { EmailVerificationFailPage } from './email/EmailVerificationFailPage';
import { EmailVerificationSuccessPage } from './email/EmailVerificationSuccessPage';
import { ErrorHandler } from './errors/ErrorHandler';
import { NotFoundPage } from './errors/NotFound/NotFoundPage';
import { FAQPage } from './faq/FAQPage';
import { LandingPage } from './landing/LandingPage';
import { offerLoader, offersOutLoader, restaurantLoader } from './loaders';
import { restaurantsLoader } from './loaders/restaurantsLoader';
import { userAccountLoader } from './loaders/userAccountLoader';
import { OfferOutDetailsPage } from './offers/OfferOutDetailsPage';
import { OffersInPage } from './offers/OffersInPage';
import { OffersLayout } from './offers/OffersLayout';
import { OffersOutPage } from './offers/OffersOutPage';
import {
  ABOUT_US_PATH,
  ACCOUNT_BALANCE_PATH,
  ACCOUNT_EMAIL_PATH,
  ACCOUNT_NOTIFICATIONS_PATH,
  ACCOUNT_PAYMENT_METHODS_PATH,
  ACCOUNT_ROOT_PATH,
  CHECKOUT_CONFIRMATION_TERMINAL_PATH,
  CHECKOUT_ROOT_PATH,
  EMAIL_VERIFICATION_FAIL_PATH,
  EMAIL_VERIFICATION_SUCCESS_PATH,
  EVENT_PAGE_PATH,
  EVENT_TICKET_PURCHASE_PATH,
  FAQ_PATH,
  LANDING_PATH,
  LOGIN_PATH,
  NEW_GUEST_LANDING_MODIFY_PATH,
  NEW_GUEST_LANDING_MODIFY_RESERVE_PATH,
  NEW_GUEST_LANDING_RESERVE_PATH,
  OFFER_CHECKOUT_CONFIRMATION_TERMINAL_PATH,
  OFFER_CHECKOUT_PATH,
  OFFER_OUT_DETAILS_PATH,
  OFFER_UPDATE_ROOT_PATH,
  OFFERS_IN_PATH,
  OFFERS_OUT_PATH,
  OFFERS_ROOT_PATH,
  PAY_LINK_PATH,
  PLAID_AUTH_TERMINAL_PATH,
  PLAID_ROOT_PATH,
  PRIVACY_POLICY_PATH,
  REDIRECT_PATH,
  OLD_RESERVATION_DETAILS_PATH,
  OLD_RESTAURANT_ROOT_PATH,
  RESERVATION_DETAILS_PATH,
  RESERVATION_EDIT_PATH,
  RESERVATIONS_CONFIRMED_PATH,
  RESERVATIONS_INACTIVE_PATH,
  RESERVATIONS_ROOT_PATH,
  RESTAURANT_DEMO_PATH,
  RESTAURANT_ROOT_PATH,
  RESTAURANTS_PATH,
  TERMS_OF_SERVICE_PATH,
  NEW_GUEST_LANDING_OFFER_PATH,
} from './paths';
import { ReservationDetailsPage } from './reservationDetails/ReservationDetailsPage';
import { ConfirmedPage } from './reservations/ConfirmedPage';
import { InactivePage } from './reservations/InactivePage';
import { ReservationsLayout } from './reservations/ReservationsLayout';
import { IsDiscoverable } from './restaurant/IsDiscoverable';
import { NewGuestLandingLayout } from './newGuestLanding/NewGuestLandingLayout';
import { RestaurantAboutPage } from './restaurant/RestaurantAboutPage';
import { RestaurantsPage } from './restaurant/RestaurantsPage';
import { Root } from './Root';
import { TicketedEventPageContainer } from './restaurantEvents/TicketedEventPage';
import { EventCheckoutPage } from './restaurantEvents/EventCheckoutPage';
import { CheckoutConfirmationPage } from './checkout/CheckoutConfirmationPage';
import { ReservationEditPage } from './reservationEdit/ReservationEditPage';
import { ReservationEditCheckoutPage } from './reservationEdit/ReservationEditCheckoutPage';
import { getCurrentEnvironmentOrDefault } from '@shared/featureFlags/featureFlagContextFactory';

const environment = getCurrentEnvironmentOrDefault();
const isNewGuestLandingEnabled =
  featureFlagConfig[environment].newGuestLandingPage;
const isRedesignedTicketedEventPageEnabled =
  featureFlagConfig[environment].redesignedTicketedEventPage;
const legacyRestaurantRootPath = isNewGuestLandingEnabled
  ? OLD_RESTAURANT_ROOT_PATH
  : RESTAURANT_ROOT_PATH;
const legacyReservationDetailsPath = isNewGuestLandingEnabled
  ? OLD_RESERVATION_DETAILS_PATH
  : RESERVATION_DETAILS_PATH;

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />} errorElement={<ErrorHandler withoutLayout />}>
      <Route errorElement={<ErrorHandler />}>
        <Route element={<LandingPage />} path={LANDING_PATH} />
        {isNewGuestLandingEnabled && (
          <Route
            element={<NewGuestLandingLayout />}
            id="newGuestLandingRestaurant"
            loader={restaurantLoader}
            path={RESTAURANT_ROOT_PATH}
          >
            <Route
              element={
                <AvailabilityContextProvider>
                  <GuestPriceUpdates />
                  <GuestPricingBrowseContextProvider>
                    <Outlet />
                  </GuestPricingBrowseContextProvider>
                </AvailabilityContextProvider>
              }
            >
              <Route
                index
                lazy={async () => {
                  const { NewGuestLandingPage } = await import(
                    './newGuestLanding/NewGuestLandingPage'
                  );
                  return { Component: NewGuestLandingPage };
                }}
              />
              <Route
                path={NEW_GUEST_LANDING_RESERVE_PATH}
                lazy={async () => {
                  const { NewGuestLandingPage } = await import(
                    './newGuestLanding/NewGuestLandingPage'
                  );
                  return { Component: NewGuestLandingPage };
                }}
              />
              <Route
                path={NEW_GUEST_LANDING_OFFER_PATH}
                lazy={async () => {
                  const { NewGuestLandingPage } = await import(
                    './newGuestLanding/NewGuestLandingPage'
                  );
                  return { Component: NewGuestLandingPage };
                }}
              />
              <Route
                path={NEW_GUEST_LANDING_MODIFY_RESERVE_PATH}
                lazy={async () => {
                  const { NewGuestLandingPage } = await import(
                    './newGuestLanding/NewGuestLandingPage'
                  );
                  return { Component: NewGuestLandingPage };
                }}
              />
              <Route
                path={NEW_GUEST_LANDING_MODIFY_PATH}
                lazy={async () => {
                  const { NewGuestLandingPage } = await import(
                    './newGuestLanding/NewGuestLandingPage'
                  );
                  return { Component: NewGuestLandingPage };
                }}
              />
            </Route>
            {isRedesignedTicketedEventPageEnabled && (
              <Route path={EVENT_TICKET_PURCHASE_PATH}>
                <Route
                  index
                  lazy={async () => {
                    const { EventTicketPurchasePageContainer } = await import(
                      './restaurantEvents/ticketPurchase/EventTicketPurchasePage'
                    );
                    return { Component: EventTicketPurchasePageContainer };
                  }}
                />
                <Route
                  path={CHECKOUT_CONFIRMATION_TERMINAL_PATH}
                  element={<CheckoutConfirmationPage />}
                />
              </Route>
            )}
          </Route>
        )}
        {isNewGuestLandingEnabled && (
          <Route
            element={<NewGuestLandingLayout />}
            id="newGuestLandingReservation"
            loader={restaurantLoader}
            path={`${RESERVATIONS_ROOT_PATH}${RESTAURANT_ROOT_PATH}`}
          >
            <Route
              path=":reservationId"
              lazy={async () => {
                const { Confirmation } = await import(
                  './newGuestLanding/screens/Confirmation'
                );
                return { Component: Confirmation };
              }}
            />
            <Route
              path=":reservationId/offers/:offerId"
              lazy={async () => {
                const { IncomingOfferDetails } = await import(
                  './newGuestLanding/screens/IncomingOfferDetails'
                );
                return { Component: IncomingOfferDetails };
              }}
            />
            <Route
              path=":reservationId/offers/:offerId/sold"
              lazy={async () => {
                const { OfferSold } = await import(
                  './newGuestLanding/screens/OfferSold'
                );
                return { Component: OfferSold };
              }}
            />
          </Route>
        )}
        <Route element={<LoginPage />} path={LOGIN_PATH} />
        {process.env.ENV !== PRODUCTION && (
          <Route
            element={<RestaurantsPage />}
            path={RESTAURANTS_PATH}
            loader={restaurantsLoader}
          />
        )}
        <Route
          element={<EmailVerificationSuccessPage />}
          path={EMAIL_VERIFICATION_SUCCESS_PATH}
        />
        <Route
          element={<EmailVerificationFailPage />}
          path={EMAIL_VERIFICATION_FAIL_PATH}
        />
        <Route element={<AccountsLayout />} path={ACCOUNT_ROOT_PATH}>
          <Route element={<Navigate to={ACCOUNT_NOTIFICATIONS_PATH} />} index />
          <Route
            path={ACCOUNT_NOTIFICATIONS_PATH}
            element={<NotificationsPage />}
          />
          <Route
            path={ACCOUNT_BALANCE_PATH}
            element={<BalancePage />}
            loader={userAccountLoader}
          />
          <Route
            path={ACCOUNT_PAYMENT_METHODS_PATH}
            element={<PaymentMethodsPage />}
          />
          <Route path={ACCOUNT_EMAIL_PATH} element={<EmailPage />} />
        </Route>
        <Route element={<FAQPage />} path={FAQ_PATH} />
        <Route element={<IsAuthenticated />} path={PLAID_ROOT_PATH}>
          <Route element={<Navigate to={PLAID_AUTH_TERMINAL_PATH} />} index />
          <Route
            path={PLAID_AUTH_TERMINAL_PATH}
            element={
              <FetchLinkToken>
                <PlaidLink />
              </FetchLinkToken>
            }
          />
          <Route element={<PlaidLink withRedirect />} path={REDIRECT_PATH} />
        </Route>
        <Route
          lazy={async () => {
            const { AboutUsPage } = await import('./aboutUs/AboutUsPage');
            return { Component: AboutUsPage };
          }}
          path={ABOUT_US_PATH}
        />
        <Route
          path={PRIVACY_POLICY_PATH}
          lazy={async () => {
            const { PrivacyPolicyPage } = await import(
              './legal/PrivacyPolicyPage'
            );
            return { Component: PrivacyPolicyPage };
          }}
        />
        <Route
          path={TERMS_OF_SERVICE_PATH}
          lazy={async () => {
            const { TermsOfServicePage } = await import(
              './legal/TermsOfServicePage'
            );
            return {
              Component: TermsOfServicePage,
            };
          }}
        />
        <Route
          element={
            <RestaurantContextProvider>
              <AvailabilityContextProvider>
                <GuestPriceUpdates />
                <GuestPricingBrowseContextProvider>
                  <IsDiscoverable />
                </GuestPricingBrowseContextProvider>
              </AvailabilityContextProvider>
            </RestaurantContextProvider>
          }
          id="restaurant"
          loader={restaurantLoader}
          path={legacyRestaurantRootPath}
        >
          <Route
            index
            lazy={async () => {
              const { RestaurantPage } = await import(
                './restaurant/RestaurantPage'
              );
              return {
                Component: RestaurantPage,
              };
            }}
          />
          <Route
            path=":floorPlanNameSlug"
            lazy={async () => {
              const { RestaurantPage } = await import(
                './restaurant/RestaurantPage'
              );
              return { Component: RestaurantPage };
            }}
          />
          <Route path={`${EVENT_PAGE_PATH}/:eventId/*`}>
            <Route index element={<TicketedEventPageContainer />} />
            <Route path={CHECKOUT_ROOT_PATH} element={<EventCheckoutPage />} />
            <Route
              path={CHECKOUT_CONFIRMATION_TERMINAL_PATH}
              element={<CheckoutConfirmationPage />}
            />
          </Route>
          <Route element={<RestaurantAboutPage />} path="about" />
          <Route
            path={CHECKOUT_ROOT_PATH}
            lazy={async () => {
              const { CheckoutLayout } = await import(
                './checkout/CheckoutLayout'
              );
              return {
                element: <CheckoutLayout />,
              };
            }}
          >
            <Route
              index
              lazy={async () => {
                const { CheckoutPage } = await import(
                  './checkout/CheckoutPage'
                );
                return {
                  Component: CheckoutPage,
                };
              }}
            />
            <Route
              path={CHECKOUT_CONFIRMATION_TERMINAL_PATH}
              lazy={async () => {
                const { CheckoutConfirmationPage } = await import(
                  './checkout/CheckoutConfirmationPage'
                );
                return {
                  Component: CheckoutConfirmationPage,
                };
              }}
            />
          </Route>
          <Route
            element={
              <RestaurantContextProvider>
                <AvailabilityContextProvider>
                  <OfferCheckoutLayout />
                </AvailabilityContextProvider>
              </RestaurantContextProvider>
            }
            path={OFFER_CHECKOUT_PATH}
          >
            <Route element={<OfferCheckoutPage />} index />
            <Route
              element={<OfferCheckoutConfirmationPage />}
              path={OFFER_CHECKOUT_CONFIRMATION_TERMINAL_PATH}
            />
          </Route>
        </Route>
        <Route
          element={
            <RestaurantContextProvider>
              <AvailabilityContextProvider>
                <OfferCheckoutLayout />
              </AvailabilityContextProvider>
            </RestaurantContextProvider>
          }
          path={OFFER_UPDATE_ROOT_PATH}
        >
          <Route element={<OfferCheckoutPage />} index />
          <Route
            element={<OfferCheckoutConfirmationPage />}
            path={OFFER_CHECKOUT_CONFIRMATION_TERMINAL_PATH}
          />
        </Route>
        <Route element={<ReservationsLayout />} path={RESERVATIONS_ROOT_PATH}>
          <Route
            element={<Navigate replace to={RESERVATIONS_CONFIRMED_PATH} />}
            index
          />
          <Route
            element={<ConfirmedPage />}
            path={RESERVATIONS_CONFIRMED_PATH}
          />
          <Route element={<InactivePage />} path={RESERVATIONS_INACTIVE_PATH} />
        </Route>
        <Route
          path={`${legacyReservationDetailsPath}/*`}
          element={
            <RestaurantContextProvider>
              <Outlet />
            </RestaurantContextProvider>
          }
        >
          <Route element={<ReservationDetailsPage />} index />
          <Route
            path={`${RESERVATION_EDIT_PATH}/*`}
            element={
              <AvailabilityContextProvider>
                <GuestPricingBrowseContextProvider>
                  <Outlet />
                </GuestPricingBrowseContextProvider>
              </AvailabilityContextProvider>
            }
          >
            <Route element={<ReservationEditPage />} index />
            <Route path={`${CHECKOUT_ROOT_PATH}/*`}>
              <Route element={<ReservationEditCheckoutPage />} index />
              <Route
                element={<CheckoutConfirmationPage />}
                path={CHECKOUT_CONFIRMATION_TERMINAL_PATH}
              />
            </Route>
          </Route>
        </Route>
        <Route element={<IsAuthenticated />}>
          <Route
            path={PAY_LINK_PATH}
            lazy={async () => {
              const { CompletePaymentLayout } = await import(
                './checkout/CompletePaymentLayout'
              );
              return { element: <CompletePaymentLayout /> };
            }}
          >
            <Route
              index
              lazy={async () => {
                const { PayLinkPage } = await import('./checkout/PayLinkPage');
                return { Component: PayLinkPage };
              }}
            />
            <Route
              path={CHECKOUT_CONFIRMATION_TERMINAL_PATH}
              lazy={async () => {
                const { CheckoutConfirmationPage } = await import(
                  './checkout/CheckoutConfirmationPage'
                );
                return { Component: CheckoutConfirmationPage };
              }}
            />
          </Route>
        </Route>
        <Route element={<OffersLayout />} path={OFFERS_ROOT_PATH}>
          <Route element={<Navigate replace to={OFFERS_OUT_PATH} />} index />
          <Route
            element={<OffersOutPage />}
            path={OFFERS_OUT_PATH}
            loader={offersOutLoader}
          />
          <Route element={<OffersInPage />} path={OFFERS_IN_PATH} />
        </Route>
        <Route
          element={<OfferOutDetailsPage />}
          loader={offerLoader}
          path={OFFER_OUT_DETAILS_PATH}
        />
        <Route
          path={RESTAURANT_DEMO_PATH}
          element={<Navigate to="/" replace />}
        />
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </Route>,
  ),
);
